import { getResizedCDNAsset } from 'utils/globalUtils';

function paginate(arr, size) {
    return arr.reduce((acc, val, i) => {
        const idx = Math.floor(i / size);
        const page = acc[idx] || (acc[idx] = []);
        page.push(val);

        return acc;
    }, []);
}

/**
 * Determines if a link should open in a new tab based on its URL
 * Opens in a new tab if one of the following conditions is met:
 * 1. The URL is absolute and points to a different domain than the current window
 * 2. The URL is relative and does not contain '/shop'
 *
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the link should open in a new tab
 */
function shouldOpenLinkInNewTab(url) {
    if (!url) return false;

    // Check if it's an absolute URL with a protocol
    if (url.match(/^https?:\/\//i)) {
        try {
            // Extract domain from URL
            const urlObj = new URL(url);
            // If hostname is different from current window, open in new tab
            if (urlObj.hostname !== window.location.hostname) {
                return true;
            }
            // Same domain, check for shop path
            return !url.includes('/shop');
        } catch (e) {
            // If URL parsing fails, assume it should open in a new tab
            return true;
        }
    }

    // It's a relative URL, check for /shop
    return !url.includes('/shop');
}

/**
 * Get a v2 Carousel's image URL
 *
 * NOTE: v2 Carousels temporarily prioritize the medium image size,
 * as it is unused by v1 carousels and we need unique aspect ratios for v2 carousel images
 * while we test this page.
 *
 * When the medium image size is used, we'll render it on all screen sizes, until we are on the new BE data.
 *
 * @param {Object} carouselData - The carousel data object containing image URLs
 * @param {boolean} mobile - Flag indicating if the request is for a mobile device
 * @param {Object} [options] - Optional resize dimensions for images
 * @param {number} [options.width] - CDN resize width (omit to use the URL as-is)
 * @param {number} [options.height] - CDN resize height (omit to use the URL as-is)
 * @returns {string|undefined} The URL of the appropriate carousel image, or undefined if no image is available
 */
function getCarouselImage(carouselData, mobile, { height, width } = {}) {
    const data = carouselData || {};

    let carouselImage = data.photoUrlMedium;

    if (carouselImage) {
        if (width !== undefined || height !== undefined) {
            carouselImage = getResizedCDNAsset({
                url: carouselImage,
                ...(width !== undefined && { width }),
                ...(height !== undefined && { height }),
            });
        }
    } else {
        carouselImage = mobile ? data.photoUrlSmall : data.photoUrlLarge;
    }

    return carouselImage;
}

/**
 * Handles navigation in the native app by routing different URL patterns to appropriate native screens
 *
 * This function intercepts navigation requests when running in a React Native WebView context
 * and routes them to native app screens or handles them appropriately based on the URL pattern.
 *
 * @param {string} href - The URL to navigate to. Can be relative or absolute.
 *                        - Cart URLs (/cart) navigate to the native Cart screen
 *                        - Account promotions URLs (/account/promotions) open in AccountWebview
 *                        - Product URLs (/shop/product/) are handled by the WebView directly
 *                        - All other URLs are routed through the ShopLink navigator
 */
function handleStorefrontNativeAppLink(href) {
    if (window.isNativeApp) {
        const url = href?.startsWith('http') ? href : `${window.location.origin}${href}`;

        if (href.includes('/cart')) {
            window.ReactNativeWebView?.postMessage(JSON.stringify({ navigate: 'Cart' }));
        } else if (href.includes('/account/promotions')) {
            window.ReactNativeWebView?.postMessage(JSON.stringify({ navigate: 'AccountWebview', url }));
        } else if (href.includes('/shop/product/')) {
            window.location.href = href;
        } else {
            window.ReactNativeWebView?.postMessage(JSON.stringify({ navigate: 'ShopLink', url }));
        }
    }
}

export { paginate, shouldOpenLinkInNewTab, getCarouselImage, handleStorefrontNativeAppLink };
